/**
 * CSV Export utility for transactions
 * 
 * @module csvExport
 */

/**
 * Formats a payment channel string for display.
 * Capitalizes first letter and replaces all underscores with spaces.
 * 
 * @param {string} channel - The payment channel (e.g., 'in_store', 'online_banking_transfer')
 * @returns {string} - Formatted string (e.g., 'In store', 'Online banking transfer')
 */
const formatPaymentChannel = (channel) => {
  if (!channel) return '';
  return channel.charAt(0).toUpperCase() + channel.slice(1).replace(/_/g, ' ');
};

/**
 * Returns a display name for an account for CSV export.
 * Formats as "AccountName (••••1234)" or "Unknown Account" if no identifiers exist.
 * 
 * @param {Object} account - The account object
 * @param {string} [account.name] - The account name
 * @param {string} [account.mask] - The last 4 digits of the account number
 * @returns {string} - The formatted display name
 */
const getAccountDisplayName = (account) => {
  if (!account.name && !account.mask) return 'Unknown Account';
  const accountName = account.name || 'Account';
  const maskDisplay = account.mask ? `••••${account.mask}` : '••••';
  return `${accountName} (${maskDisplay})`;
};

/**
 * Escapes a value for CSV format.
 * Handles commas, quotes, and newlines by wrapping in quotes and escaping internal quotes.
 * 
 * @param {*} value - The value to escape
 * @returns {string} - The escaped value safe for CSV
 */
const escapeCSVValue = (value) => {
  if (value === null || value === undefined) {
    return '';
  }
  
  const stringValue = String(value);
  
  // If the value contains comma, quote, or newline, wrap in quotes and escape internal quotes
  if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n') || stringValue.includes('\r')) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  
  return stringValue;
};

/**
 * Formats a date for CSV export (YYYY-MM-DD format)
 * 
 * @param {Date|string} date - The date to format
 * @returns {string} - Formatted date string
 */
const formatDateForCSV = (date) => {
  if (!date) return '';
  
  try {
    const dateObj = new Date(date);
    if (isNaN(dateObj.getTime())) return '';
    
    // Extract date part to preserve the calendar date
    const dateStr = typeof date === 'string' 
      ? date.split('T')[0]
      : dateObj.toISOString().split('T')[0];
    
    return dateStr;
  } catch {
    return '';
  }
};

/**
 * Formats an amount for CSV export
 * 
 * @param {number} amount - The amount to format
 * @returns {string} - Formatted amount string (2 decimal places)
 */
const formatAmountForCSV = (amount) => {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return '0.00';
  }
  return amount.toFixed(2);
};

/**
 * Converts transactions to CSV format
 * 
 * @param {Array<Object>} transactions - Array of transaction objects with the following structure:
 *   @param {string} [transactions[].authorizedDate] - Transaction authorized date (ISO string, preferred)
 *   @param {string} [transactions[].date] - Transaction date (ISO string, fallback)
 *   @param {string} [transactions[].merchantName] - Merchant name
 *   @param {string} [transactions[].description] - Transaction description
 *   @param {number} transactions[].amount - Transaction amount (positive = expense, negative = income per Plaid convention)
 *   @param {Object} [transactions[].budgetCategory] - Budget category object
 *   @param {string} [transactions[].budgetCategory.name] - Category name
 *   @param {string} [transactions[].category] - Category string (fallback)
 *   @param {string} [transactions[].plaidAccountId] - Plaid account ID for account lookup
 *   @param {string} [transactions[].source] - Transaction source ('manual', 'plaid', etc.)
 *   @param {boolean} [transactions[].pending] - Whether transaction is pending
 *   @param {string} [transactions[].paymentChannel] - Payment channel (e.g., 'in_store', 'online')
 * @param {Object} options - Export options
 * @param {Array} options.accounts - Array of account objects for name lookup
 * @returns {string} - CSV content as a string
 */
export const transactionsToCSV = (transactions, options = {}) => {
  const { accounts = [] } = options;
  
  // Create account lookup map for display names
  const accountMap = accounts.reduce((map, account) => {
    map[account.id] = getAccountDisplayName(account);
    return map;
  }, {});
  
  // Define CSV headers
  const headers = [
    'Date',
    'Description',
    'Merchant',
    'Amount',
    'Type',
    'Category',
    'Account',
    'Status',
    'Payment Channel'
  ];
  
  // Convert transactions to rows
  const rows = transactions.map(transaction => {
    // Get the display date (prefer authorizedDate, fall back to date)
    const displayDate = transaction.authorizedDate || transaction.date;
    
    // Get description (prefer merchantName, fall back to description)
    const description = transaction.merchantName || transaction.description || '';
    const merchant = transaction.merchantName || '';
    
    // Format amount (positive = expense, negative = income in Plaid convention)
    const amount = formatAmountForCSV(transaction.amount);
    
    // Determine transaction type
    // Plaid convention: negative amounts are income, positive amounts are expenses (counterintuitive)
    // Zero-amount transactions are treated as transfers (e.g., balance transfers, account adjustments)
    const type = transaction.amount === 0 ? 'Transfer' : (transaction.amount < 0 ? 'Income' : 'Expense');
    
    // Get category name
    const category = transaction.budgetCategory?.name || transaction.category || 'Uncategorized';
    
    // Get account display name
    const accountName = transaction.plaidAccountId 
      ? (accountMap[transaction.plaidAccountId] || 'Unknown Account')
      : (transaction.source === 'manual' ? 'Manual Entry' : 'Unknown Account');
    
    // Get status
    const status = transaction.pending ? 'Pending' : 'Posted';
    
    // Get payment channel (formatted for display)
    const paymentChannel = formatPaymentChannel(transaction.paymentChannel);
    
    return [
      escapeCSVValue(formatDateForCSV(displayDate)),
      escapeCSVValue(description),
      escapeCSVValue(merchant),
      escapeCSVValue(amount),
      escapeCSVValue(type),
      escapeCSVValue(category),
      escapeCSVValue(accountName),
      escapeCSVValue(status),
      escapeCSVValue(paymentChannel)
    ];
  });
  
  // Combine headers and rows
  const csvContent = [
    headers.map(escapeCSVValue).join(','),
    ...rows.map(row => row.join(','))
  ].join('\n');
  
  return csvContent;
};

/**
 * Triggers a CSV file download in the browser
 * 
 * @param {string} csvContent - The CSV content to download
 * @param {string} filename - The filename for the download (without extension)
 * @returns {boolean} - True if download was triggered successfully, false if an error occurred
 */
export const downloadCSV = (csvContent, filename = 'transactions') => {
  try {
    // Add BOM for Excel compatibility with UTF-8
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    
    // Create download link
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}.csv`);
    link.style.display = 'none';
    
    // Append to document, click, and cleanup
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Revoke the URL to free memory
    setTimeout(() => URL.revokeObjectURL(url), 100);
    return true;
  } catch (err) {
    // Error is logged by caller if needed (no PII in CSV download errors)
    return false;
  }
};

/**
 * Generates a filename for the CSV export based on current date and filters
 * 
 * @param {Object} filters - Applied filters
 * @returns {string} - Generated filename
 */
export const generateCSVFilename = (filters = {}) => {
  const now = new Date();
  const dateStr = now.toISOString().split('T')[0]; // YYYY-MM-DD
  
  let filename = `budgit-transactions-${dateStr}`;
  
  // Add filter info to filename if applicable
  // Activity.js uses 'dateFilter' as the property name
  if (filters.dateFilter && filters.dateFilter !== 'all' && filters.dateFilter !== 'any') {
    filename += `-${filters.dateFilter}`;
  }
  
  return filename;
};

